/**
 * Age Gate Modal Component
 * 
 * Displays a modal requiring users to confirm they are 18+ before viewing content.
 * Stores confirmation in localStorage to persist across sessions.
 */

import React, { useState, useEffect } from 'react';

const STORAGE_KEY = 'pleasance_age_verified';

function AgeGateModal() {
    const [isVerified, setIsVerified] = useState(null); // null = loading, true/false = verified status

    useEffect(() => {
        // Check localStorage on mount
        const verified = localStorage.getItem(STORAGE_KEY);
        setIsVerified(verified === 'true');
    }, []);

    const handleConfirm = () => {
        localStorage.setItem(STORAGE_KEY, 'true');
        setIsVerified(true);
    };

    const handleReject = () => {
        // Redirect to a safe exit page (Google in this case)
        window.location.href = 'https://www.google.com';
    };

    // Don't render anything while checking localStorage
    if (isVerified === null) {
        return null;
    }

    // User has already verified - don't show modal
    if (isVerified) {
        return null;
    }

    // Show the age gate modal
    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm">
            {/* Modal Container */}
            <div className="relative max-w-md w-full mx-4 bg-charcoal-800 border border-wine-500/30 rounded-2xl shadow-2xl p-8">
                {/* Logo/Branding */}
                <div className="text-center mb-6">
                    <h1 className="text-3xl font-serif text-wine-400 tracking-wide">
                        Pleasance
                    </h1>
                    <p className="text-ivory-400 text-sm mt-1 tracking-wider uppercase">
                        Play Without Shame
                    </p>
                </div>

                {/* Warning Content */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-wine-500/20 mb-4">
                        <span className="text-3xl">🔞</span>
                    </div>
                    <h2 className="text-xl text-ivory-100 font-medium mb-3">
                        Age Verification Required
                    </h2>
                    <p className="text-ivory-400 text-sm leading-relaxed">
                        This website contains adult content related to kinks, BDSM, and sexual education.
                        You must be <span className="text-wine-400 font-semibold">18 years or older</span> to enter.
                    </p>
                </div>

                {/* Legal Notice */}
                <div className="bg-charcoal-900/50 rounded-lg p-4 mb-6 text-xs text-ivory-500">
                    By entering, you confirm that you are at least 18 years old and that viewing adult content is legal in your jurisdiction.
                    You agree to our <a href="/terms" className="text-wine-400 hover:underline">Terms of Service</a> and <a href="/privacy" className="text-wine-400 hover:underline">Privacy Policy</a>.
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4">
                    <button
                        onClick={handleReject}
                        className="flex-1 px-6 py-3 rounded-lg border border-ivory-500/30 text-ivory-400 hover:bg-ivory-500/10 transition-colors font-medium"
                    >
                        Exit
                    </button>
                    <button
                        onClick={handleConfirm}
                        className="flex-1 px-6 py-3 rounded-lg bg-wine-600 hover:bg-wine-500 text-white transition-colors font-medium shadow-lg shadow-wine-900/50"
                    >
                        I am 18 or older
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AgeGateModal;
